import { useEffect } from 'react';
import { ActionIcon, Alert, Badge, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ExternalLink } from 'lucide-react';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { OperatorBillingViewModel } from './OperatorBillingViewModel';
import type { OperatorInvoice } from './OperatorBillingService';

const STATUS_COLOR: Record<string, string> = { draft: 'gray', open: 'blue', paid: 'green', past_due: 'orange', void: 'red' };

function money(minor: number, currency: string): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(minor / 100);
}

/** Operator Billing — the invoices ledger across all tenants. */
export default function OperatorBillingPage(): React.ReactElement {
    const vm = useViewModel(OperatorBillingViewModel);

    useEffect(() => { void vm.load(); }, [vm]);

    const columns: MegaGridColumn<OperatorInvoice>[] = [
        { key: 'number', header: 'Invoice', sortValue: (r) => r.number, render: (r) => <Text size="sm" fw={500}>{r.number}</Text> },
        { key: 'tenant', header: 'Tenant', sortValue: (r) => r.tenantName, render: (r) => <Text size="sm">{r.tenantName}</Text> },
        { key: 'plan', header: 'Plan', sortValue: (r) => r.planName ?? '', render: (r) => <Text size="sm" c="dimmed" tt="capitalize">{r.planName ?? '—'}</Text> },
        { key: 'status', header: 'Status', sortValue: (r) => r.status, render: (r) => <Badge size="sm" variant="light" color={STATUS_COLOR[r.status] ?? 'gray'}>{r.status.replace('_', ' ')}</Badge> },
        { key: 'amount', header: 'Amount', align: 'right', sortValue: (r) => r.totalMinor, render: (r) => <Text size="sm">{money(r.totalMinor, r.currency)}</Text> },
        { key: 'issued', header: 'Issued', sortValue: (r) => r.issuedAt ?? '', render: (r) => <Text size="sm" c="dimmed">{r.issuedAt?.slice(0, 10) ?? '—'}</Text> },
        { key: 'paid', header: 'Paid', sortValue: (r) => r.paidAt ?? '', render: (r) => <Text size="sm" c="dimmed">{r.paidAt?.slice(0, 10) ?? '—'}</Text> },
        {
            key: 'download', header: '', align: 'right', render: (r) => (
                <Tooltip label="Open PDF" withArrow>
                    <ActionIcon variant="subtle" color="gray" aria-label={`Open ${r.number}`} onClick={() => void openPdf(r)}>
                        <ExternalLink size={16} />
                    </ActionIcon>
                </Tooltip>
            ),
        },
    ];

    const openPdf = async (r: OperatorInvoice): Promise<void> => {
        try {
            await vm.openInvoice(r.tenantId, r.id);
        } catch (e) {
            notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed to open invoice' });
        }
    };

    return (
        <Stack gap="md">
            <PageHeaderComponent title="Billing" subtitle="Invoices and payment activity across tenants." />
            {vm.error && <Alert color="red" title="Failed to load">{vm.error}</Alert>}
            <MegaGrid
                persistenceKey="operator-invoices"
                columns={columns}
                rows={vm.invoices}
                getRowKey={(r) => `${r.tenantId}:${r.number}`}
                defaultSort={{ key: 'issued', dir: 'desc' }}
                searchAccessor={(r) => `${r.number} ${r.tenantName} ${r.planName ?? ''}`}
                searchPlaceholder="Search invoices"
                emptyMessage={vm.loading ? 'Loading…' : 'No invoices yet'}
                loading={vm.loading}
                clientPageSize={25}
            />
        </Stack>
    );
}
